import { useState } from 'react';
import { Link, router } from '@inertiajs/react';

export default function QuotationTab({ query, quotations = [], statuses = {}, statusColors = {} }) {
    const [selectedIds, setSelectedIds] = useState([]);

    const handleDelete = (quotation) => {
        if (confirm(`Are you sure you want to delete ${quotation.quotation_number}?`)) {
            router.delete(`/queries/${query.id}/quotations/${quotation.id}`);
        }
    };

    const handleDuplicate = (quotation) => {
        router.post(`/queries/${query.id}/quotations/${quotation.id}/duplicate`);
    };

    const handleStatusChange = (quotation, status) => {
        router.patch(`/queries/${query.id}/quotations/${quotation.id}/status`, {
            status,
        });
    };

    const getStatusColor = (status) => {
        const colors = {
            draft: 'secondary',
            sent: 'info',
            accepted: 'success',
            rejected: 'danger',
            expired: 'warning',
        };
        return colors[status] || 'secondary';
    };

    const formatCurrency = (amount, currency) => {
        if (!amount) return '-';
        const symbol = currency?.symbol || '₹';
        return `${symbol}${parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
    };

    const statusEntries = Object.entries(statuses || {});

    return (
        <div className="qd-quotation-tab">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h5 className="mb-1">Quotations</h5>
                    <p className="text-muted mb-0 small">
                        {quotations.length} quotation{quotations.length !== 1 ? 's' : ''} created
                    </p>
                </div>
                <Link
                    href={`/queries/${query.id}/quotations/create`}
                    className="btn btn-primary"
                >
                    <i className="bi bi-plus-lg me-1"></i>
                    New Quotation
                </Link>
            </div>

            {/* Quotations List */}
            {quotations.length === 0 ? (
                <div className="text-center py-5">
                    <div className="mb-3">
                        <i className="bi bi-file-earmark-text display-4 text-muted"></i>
                    </div>
                    <h6 className="text-muted mb-2">No quotations yet</h6>
                    <p className="text-muted small mb-3">
                        Create your first quotation to start building the itinerary
                    </p>
                    <Link
                        href={`/queries/${query.id}/quotations/create`}
                        className="btn btn-primary btn-sm"
                    >
                        <i className="bi bi-plus-lg me-1"></i>
                        Create Quotation
                    </Link>
                </div>
            ) : (
                <div className="row g-3">
                    {quotations.map((quotation, index) => (
                        <div key={quotation.id} className="col-12">
                            <div className="card border-0 shadow-sm">
                                <div className="card-body">
                                    <div className="row align-items-center">
                                        {/* Quotation Info */}
                                        <div className="col-lg-4">
                                            <div className="d-flex align-items-center">
                                                <div
                                                    className="qd-quotation-icon me-3"
                                                    style={{
                                                        backgroundColor: quotation.status === 'accepted' ? '#10b981' : '#6366f1'
                                                    }}
                                                >
                                                    <i className="bi bi-file-earmark-text"></i>
                                                </div>
                                                <div>
                                                    <h6 className="mb-1">
                                                        <Link
                                                            href={`/queries/${query.id}/quotations/${quotation.id}/edit`}
                                                            className="text-decoration-none text-dark"
                                                        >
                                                            {quotation.quotation_number}
                                                        </Link>
                                                        {quotation.is_final && (
                                                            <span className="badge bg-success-subtle text-success ms-2 small">
                                                                Final
                                                            </span>
                                                        )}
                                                    </h6>
                                                    <p className="text-muted small mb-0">
                                                        {quotation.title || 'Untitled Quotation'}
                                                    </p>
                                                    <div className="text-muted small">
                                                        Version {quotation.version || 1} • {quotation.items_count || 0} services
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Travel Details */}
                                        <div className="col-lg-2">
                                            <div className="small text-muted mb-1">Travel</div>
                                            <div className="fw-medium">
                                                {quotation.travel_date
                                                    ? new Date(quotation.travel_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
                                                    : '-'
                                                }
                                            </div>
                                            <div className="small text-muted">
                                                {quotation.nights || 0}N/{(quotation.nights || 0) + 1}D
                                            </div>
                                        </div>

                                        {/* Pax */}
                                        <div className="col-lg-2">
                                            <div className="small text-muted mb-1">Travelers</div>
                                            <div className="fw-medium">
                                                {quotation.adults || 0} Adults
                                            </div>
                                            <div className="small text-muted">
                                                {(quotation.children || 0) > 0 && `${quotation.children} Children`}
                                                {(quotation.infants || 0) > 0 && `, ${quotation.infants} Infants`}
                                            </div>
                                        </div>

                                        {/* Pricing */}
                                        <div className="col-lg-2">
                                            <div className="small text-muted mb-1">Selling Price</div>
                                            <div className="fw-bold text-primary">
                                                {formatCurrency(quotation.total_selling, quotation.currency)}
                                            </div>
                                            <div className="small text-muted">
                                                Cost: {formatCurrency(quotation.total_cost, quotation.currency)}
                                            </div>
                                        </div>

                                        {/* Status & Actions */}
                                        <div className="col-lg-2 text-end">
                                            <div className="dropdown d-inline-block me-2">
                                                <button
                                                    className={`btn btn-sm btn-${getStatusColor(quotation.status)}-subtle dropdown-toggle`}
                                                    type="button"
                                                    data-bs-toggle="dropdown"
                                                >
                                                    {(statuses && statuses[quotation.status]) || quotation.status || 'Draft'}
                                                </button>
                                                <ul className="dropdown-menu dropdown-menu-end">
                                                    {statusEntries.map(([key, label]) => (
                                                        <li key={key}>
                                                            <button
                                                                className={`dropdown-item ${quotation.status === key ? 'active' : ''}`}
                                                                onClick={() => handleStatusChange(quotation, key)}
                                                            >
                                                                {label}
                                                            </button>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>

                                            <div className="dropdown d-inline-block">
                                                <button
                                                    className="btn btn-sm btn-light"
                                                    type="button"
                                                    data-bs-toggle="dropdown"
                                                >
                                                    <i className="bi bi-three-dots-vertical"></i>
                                                </button>
                                                <ul className="dropdown-menu dropdown-menu-end">
                                                    <li>
                                                        <Link
                                                            href={`/queries/${query.id}/quotations/${quotation.id}/edit`}
                                                            className="dropdown-item"
                                                        >
                                                            <i className="bi bi-pencil me-2"></i>
                                                            Edit
                                                        </Link>
                                                    </li>
                                                    <li>
                                                        <Link
                                                            href={`/queries/${query.id}/quotations/${quotation.id}`}
                                                            className="dropdown-item"
                                                        >
                                                            <i className="bi bi-eye me-2"></i>
                                                            Preview
                                                        </Link>
                                                    </li>
                                                    <li>
                                                        <button
                                                            className="dropdown-item"
                                                            onClick={() => handleDuplicate(quotation)}
                                                        >
                                                            <i className="bi bi-copy me-2"></i>
                                                            Duplicate
                                                        </button>
                                                    </li>
                                                    <li><hr className="dropdown-divider" /></li>
                                                    <li>
                                                        <button
                                                            className="dropdown-item text-danger"
                                                            onClick={() => handleDelete(quotation)}
                                                        >
                                                            <i className="bi bi-trash me-2"></i>
                                                            Delete
                                                        </button>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Meta info */}
                                    <div className="mt-3 pt-3 border-top d-flex justify-content-between align-items-center">
                                        <div className="small text-muted">
                                            Created by {quotation.created_by?.name || 'Unknown'} on{' '}
                                            {new Date(quotation.created_at).toLocaleDateString('en-IN', {
                                                day: 'numeric',
                                                month: 'short',
                                                year: 'numeric',
                                            })}
                                        </div>
                                        <div className="small text-muted">
                                            {quotation.sent_at && (
                                                <span className="me-3">
                                                    <i className="bi bi-send me-1"></i>
                                                    Sent {new Date(quotation.sent_at).toLocaleDateString('en-IN')}
                                                </span>
                                            )}
                                            {quotation.accepted_at && (
                                                <span className="text-success">
                                                    <i className="bi bi-check-circle me-1"></i>
                                                    Accepted {new Date(quotation.accepted_at).toLocaleDateString('en-IN')}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
